import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  type Location,
  type NavigationType,
} from 'react-router-dom';

import type { Tenant, User } from '../types';

type PrimitiveContextValue = string | number | boolean | null;
type ContextValue =
  | PrimitiveContextValue
  | ContextValue[]
  | {
      [key: string]: ContextValue;
    };

type CaptureLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

interface OperationalCaptureOptions {
  level?: CaptureLevel;
  tags?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

interface AnnotatedSentryMetadata extends OperationalCaptureOptions {
  name?: string;
}

interface SentryBeforeSendEventLike {
  message?: string;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
  tags?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

const MAX_CONTEXT_DEPTH = 4;
const MAX_CONTEXT_ARRAY_ITEMS = 10;
const MAX_CONTEXT_STRING_LENGTH = 240;
const APP_NAME = 'contact-tel';
const APP_AREA = 'frontend';
const ROUTE_CONTEXT_NAME = 'route';
const AUTH_CONTEXT_NAME = 'auth';
const WEBSOCKET_CONTEXT_NAME = 'websocket';
const SIP_CONTEXT_NAME = 'sip';
const NOISY_ERROR_PATTERNS = [
  /AbortError: The play\(\) request was interrupted by a new load request/i,
  /The play\(\) request was interrupted by a new load request/i,
];

let sentryInitialized = false;

function parseSampleRate(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  if (parsed < 0) {
    return 0;
  }
  if (parsed > 1) {
    return 1;
  }
  return parsed;
}

function trimString(value: string): string {
  if (value.length <= MAX_CONTEXT_STRING_LENGTH) {
    return value;
  }
  return `${value.slice(0, MAX_CONTEXT_STRING_LENGTH)}...`;
}

function normalizeTagValue(value: unknown): string {
  if (value == null) {
    return 'none';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'nan';
  }
  const normalized = String(value).trim();
  return normalized ? trimString(normalized) : 'none';
}

function sanitizeForContext(value: unknown, depth = 0): ContextValue {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return trimString(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (depth >= MAX_CONTEXT_DEPTH) {
    return '[truncated]';
  }
  if (Array.isArray(value)) {
    return value.slice(0, MAX_CONTEXT_ARRAY_ITEMS).map((entry) => sanitizeForContext(entry, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, ContextValue>>((acc, [key, entry]) => {
      acc[key] = sanitizeForContext(entry, depth + 1);
      return acc;
    }, {});
  }
  return trimString(String(value));
}

function sanitizeSearchKeys(search: string): string[] {
  try {
    return Array.from(new URLSearchParams(search).keys()).sort();
  } catch {
    return [];
  }
}

function deriveFeatureArea(pathname: string): string {
  const normalizedPath = String(pathname || '/').trim() || '/';
  if (normalizedPath === '/') {
    return 'dashboard';
  }
  const [firstSegment] = normalizedPath.replace(/^\/+/, '').split('/');
  return firstSegment || 'unknown';
}

function getEventSummary(event: SentryBeforeSendEventLike): string {
  const exceptionSummary = event.exception?.values
    ?.map((value) => `${value.type || 'UnknownError'}:${value.value || ''}`)
    .join(' | ');
  return [event.message || '', exceptionSummary || ''].filter(Boolean).join(' | ');
}

function shouldDropEvent(event: SentryBeforeSendEventLike): boolean {
  const summary = getEventSummary(event);
  return NOISY_ERROR_PATTERNS.some((pattern) => pattern.test(summary));
}

function maybeAnnotatePromiseRejection(event: SentryBeforeSendEventLike) {
  const summary = getEventSummary(event);
  if (!/Object captured as promise rejection with keys:/i.test(summary)) {
    return;
  }
  const keys = summary.split('keys:')[1]?.trim() || 'unknown';
  event.tags = {
    ...(event.tags || {}),
    promise_rejection_shape: 'object',
  };
  event.extra = {
    ...(event.extra || {}),
    promise_rejection_keys: keys,
  };
  event.fingerprint = event.fingerprint || ['promise-rejection-object', keys];
}

function applyAnnotatedMetadata(event: SentryBeforeSendEventLike, metadata?: AnnotatedSentryMetadata) {
  if (!metadata) {
    return;
  }
  if (metadata.fingerprint?.length) {
    event.fingerprint = metadata.fingerprint;
  }
  if (metadata.tags) {
    event.tags = {
      ...(event.tags || {}),
      ...Object.entries(metadata.tags).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = normalizeTagValue(value);
        return acc;
      }, {}),
    };
  }
  if (metadata.extra) {
    event.extra = {
      ...(event.extra || {}),
      ...Object.entries(metadata.extra).reduce<Record<string, ContextValue>>((acc, [key, value]) => {
        acc[key] = sanitizeForContext(value);
        return acc;
      }, {}),
    };
  }
  if (metadata.contexts) {
    event.contexts = {
      ...(event.contexts || {}),
      ...Object.entries(metadata.contexts).reduce<Record<string, Record<string, unknown>>>((acc, [key, value]) => {
        acc[key] = sanitizeForContext(value) as Record<string, unknown>;
        return acc;
      }, {}),
    };
  }
}

function sanitizeBreadcrumbData(data: Record<string, unknown> | undefined): Record<string, ContextValue> | undefined {
  if (!data) {
    return undefined;
  }
  return Object.entries(data).reduce<Record<string, ContextValue>>((acc, [key, value]) => {
    acc[key] = sanitizeForContext(value);
    return acc;
  }, {});
}

function extractAnnotatedMetadata(originalException: unknown): AnnotatedSentryMetadata | undefined {
  if (!originalException || typeof originalException !== 'object') {
    return undefined;
  }
  return (originalException as { __contactTelSentry?: AnnotatedSentryMetadata }).__contactTelSentry;
}

export function initializeSentry() {
  if (sentryInitialized) {
    return false;
  }

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (!sentryDsn) {
    return false;
  }

  const tracesSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.05);
  const replaysSessionSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE, 0.01);
  const replaysOnErrorSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE, 0.25);

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'develop',
    release: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    normalizeDepth: 6,
    initialScope: {
      tags: {
        app_name: APP_NAME,
        app_area: APP_AREA,
        runtime: 'browser',
      },
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && typeof breadcrumb.message === 'string') {
        if (breadcrumb.message.startsWith('[SIP]') || breadcrumb.message.startsWith('[WS]')) {
          return null;
        }
      }
      return {
        ...breadcrumb,
        data: sanitizeBreadcrumbData(breadcrumb.data as Record<string, unknown> | undefined),
      };
    },
    beforeSend(event, hint) {
      if (shouldDropEvent(event)) {
        return null;
      }

      maybeAnnotatePromiseRejection(event);
      applyAnnotatedMetadata(event, extractAnnotatedMetadata(hint?.originalException));

      return event;
    },
  });

  sentryInitialized = true;
  return true;
}

export function annotateErrorForSentry<T extends Error>(error: T, metadata: AnnotatedSentryMetadata): T {
  Object.defineProperty(error, '__contactTelSentry', {
    configurable: true,
    enumerable: false,
    writable: true,
    value: metadata,
  });
  if (metadata.name) {
    error.name = metadata.name;
  }
  return error;
}

export function createOperationalError(message: string, metadata: AnnotatedSentryMetadata = {}): Error {
  const error = new Error(message);
  return annotateErrorForSentry(error, metadata);
}

export function addOperationalBreadcrumb(options: {
  category: string;
  message: string;
  level?: CaptureLevel;
  data?: Record<string, unknown>;
  type?: string;
}) {
  Sentry.addBreadcrumb({
    category: options.category,
    message: options.message,
    level: options.level || 'info',
    type: options.type || 'default',
    data: sanitizeBreadcrumbData(options.data),
  });
}

export function captureFrontendException(error: unknown, options: OperationalCaptureOptions = {}) {
  Sentry.withScope((scope) => {
    scope.setLevel(options.level || 'error');
    if (options.tags) {
      Object.entries(options.tags).forEach(([key, value]) => {
        scope.setTag(key, normalizeTagValue(value));
      });
    }
    if (options.contexts) {
      Object.entries(options.contexts).forEach(([key, value]) => {
        scope.setContext(key, sanitizeForContext(value) as Record<string, unknown>);
      });
    }
    if (options.extra) {
      Object.entries(options.extra).forEach(([key, value]) => {
        scope.setExtra(key, sanitizeForContext(value));
      });
    }
    if (options.fingerprint?.length) {
      scope.setFingerprint(options.fingerprint);
    }
    Sentry.captureException(error);
  });
}

export function captureOperationalIssue(message: string, options: OperationalCaptureOptions = {}) {
  Sentry.withScope((scope) => {
    scope.setLevel(options.level || 'warning');
    if (options.tags) {
      Object.entries(options.tags).forEach(([key, value]) => {
        scope.setTag(key, normalizeTagValue(value));
      });
    }
    if (options.contexts) {
      Object.entries(options.contexts).forEach(([key, value]) => {
        scope.setContext(key, sanitizeForContext(value) as Record<string, unknown>);
      });
    }
    if (options.extra) {
      Object.entries(options.extra).forEach(([key, value]) => {
        scope.setExtra(key, sanitizeForContext(value));
      });
    }
    if (options.fingerprint?.length) {
      scope.setFingerprint(options.fingerprint);
    }
    Sentry.captureMessage(message);
  });
}

export function setOperationalUserContext(params: {
  user: User | null;
  currentTenant?: Tenant | null;
  isAuthenticated?: boolean;
}) {
  const { currentTenant = null, user, isAuthenticated = Boolean(user) } = params;
  if (!user || !isAuthenticated) {
    Sentry.setUser(null);
    Sentry.setTag('tenant_id', 'none');
    Sentry.setTag('tenant_scope', 'anonymous');
    Sentry.setTag('user_role', 'anonymous');
    Sentry.setContext(AUTH_CONTEXT_NAME, {
      authenticated: false,
      switched_tenant: false,
    });
    return;
  }

  const effectiveTenantId = currentTenant?.id || user.tenantId || null;
  const hasSwitchedTenant = Boolean(currentTenant?.id && user.tenantId && currentTenant.id !== user.tenantId);
  const userRole = user.isSuperuser || user.isSystemAdmin
    ? 'superuser'
    : user.isStaff
      ? 'staff'
      : 'user';

  Sentry.setUser({
    id: String(user.id),
    username: String(user.firstName || user.email || user.id),
  });
  Sentry.setTag('tenant_id', normalizeTagValue(effectiveTenantId));
  Sentry.setTag('tenant_scope', hasSwitchedTenant ? 'switched' : 'native');
  Sentry.setTag('user_role', userRole);
  Sentry.setTag('has_telephony_endpoint', normalizeTagValue(Boolean(user.telephonyEndpoint?.isActive)));
  Sentry.setContext(AUTH_CONTEXT_NAME, {
    authenticated: true,
    user_id: user.id,
    tenant_id: effectiveTenantId,
    tenant_name: currentTenant?.name || user.tenantName || null,
    switched_tenant: hasSwitchedTenant,
    is_superuser: Boolean(user.isSuperuser || user.isSystemAdmin),
    telephony_endpoint_type: user.telephonyEndpoint?.endpointType || null,
    telephony_extension: user.telephonyEndpoint?.extension || null,
  });
}

export function setRouteScopeContext(params: {
  location: Location;
  navigationType: NavigationType;
}) {
  const { location, navigationType } = params;
  const featureArea = deriveFeatureArea(location.pathname);
  const searchKeys = sanitizeSearchKeys(location.search);

  Sentry.setTag('feature_area', featureArea);
  Sentry.setTag('route_name', featureArea);
  Sentry.setContext(ROUTE_CONTEXT_NAME, {
    pathname: location.pathname,
    search_keys: searchKeys,
    has_hash: Boolean(location.hash),
    navigation_type: navigationType,
  });
}

export function setWebSocketScopeContext(params: {
  isConnected: boolean;
  isConnecting: boolean;
  error?: string | null;
  observerCount?: number;
  sharedSubscriptionCount?: number;
  canOperateInThisTab?: boolean;
}) {
  Sentry.setTag('ws_connected', normalizeTagValue(params.isConnected));
  Sentry.setTag('ws_connecting', normalizeTagValue(params.isConnecting));
  Sentry.setContext(WEBSOCKET_CONTEXT_NAME, {
    is_connected: params.isConnected,
    is_connecting: params.isConnecting,
    error: params.error || null,
    observer_count: params.observerCount ?? 0,
    shared_subscription_count: params.sharedSubscriptionCount ?? 0,
    can_operate_in_this_tab: params.canOperateInThisTab ?? null,
  });
}

export function setSipScopeContext(params: {
  extension?: string | null;
  sessionId?: string | null;
  status: string;
  connectionState: string;
  reasonCode?: string | null;
  lastVerifiedAt?: string | null;
  incomingRecoveryState?: string | null;
  canOperateInThisTab?: boolean;
  isEligible?: boolean;
}) {
  Sentry.setTag('sip_extension', normalizeTagValue(params.extension || null));
  Sentry.setTag('sip_status', normalizeTagValue(params.status));
  Sentry.setTag('sip_connection_state', normalizeTagValue(params.connectionState));
  Sentry.setContext(SIP_CONTEXT_NAME, {
    extension: params.extension || null,
    session_id: params.sessionId || null,
    status: params.status,
    connection_state: params.connectionState,
    reason_code: params.reasonCode || null,
    last_verified_at: params.lastVerifiedAt || null,
    incoming_recovery_state: params.incomingRecoveryState || null,
    can_operate_in_this_tab: params.canOperateInThisTab ?? null,
    is_eligible: params.isEligible ?? null,
  });
}

export function SentryRouteStateBridge() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRouteRef = useRef<string | null>(null);

  useEffect(() => {
    setRouteScopeContext({ location, navigationType });
    const routeSignature = `${location.pathname}|${location.search}|${location.hash}|${navigationType}`;
    if (previousRouteRef.current === routeSignature) {
      return;
    }
    previousRouteRef.current = routeSignature;
    addOperationalBreadcrumb({
      category: 'navigation',
      message: 'Route changed',
      level: 'info',
      type: 'navigation',
      data: {
        pathname: location.pathname,
        searchKeys: sanitizeSearchKeys(location.search),
        hasHash: Boolean(location.hash),
        navigationType,
        featureArea: deriveFeatureArea(location.pathname),
      },
    });
  }, [location, navigationType]);

  return null;
}

function SentryAppErrorFallback() {
  const { t } = useTranslation(['errors', 'common']);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 6,
      }}
    >
      <Alert
        severity="error"
        sx={{
          maxWidth: 560,
          width: '100%',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('errors:app.crashTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('errors:app.crashDescription')}
            </Typography>
          </Box>
          <Box>
            <Button variant="contained" onClick={() => window.location.reload()}>
              {t('common:versionGuard.reloadNow')}
            </Button>
          </Box>
        </Stack>
      </Alert>
    </Box>
  );
}

export function AppSentryErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={<SentryAppErrorFallback />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

export const __sentryTestUtils = {
  deriveFeatureArea,
  sanitizeForContext,
  shouldDropEvent,
  maybeAnnotatePromiseRejection,
  applyAnnotatedMetadata,
};
